import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import OrderSuccessModal from '../components/OrderSuccessModal';
import PaymentFailureDialog from '../components/PaymentFailureDialog';
import ShippingAddressForm from '../components/ShippingAddressForm';
import { useAuth } from '../contexts/AuthContext';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import apiService from '../services/api';

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();
  const { isNotAcceptingOrders, isComingSoon } = useSiteSettings();

  // Get order data from state
  const orderData = location.state?.orderData;

  const [deliveryType, setDeliveryType] = useState('standard');
  const [paymentMethod, setPaymentMethod] = useState('payu');
  const [shippingAddress, setShippingAddress] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successOrder, setSuccessOrder] = useState(null);
  const [showPaymentFailureDialog, setShowPaymentFailureDialog] = useState(false);
  const [paymentFailureMessage, setPaymentFailureMessage] = useState('');
  const [showOrdersDisabledDialog, setShowOrdersDisabledDialog] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [couponError, setCouponError] = useState('');
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);

  const priceBreakdown = useMemo(() => {
    if (!orderData?.totalAmount) return null;

    const subtotal = Number(orderData.totalAmount);
    const discount = discountAmount;
    const discountedSubtotal = subtotal - discount;

    // Get delivery cost from product's deliveryInfo
    let shipping = 0;
    if (deliveryType === 'self-pickup') {
      shipping = 0;
    } else if (orderData.product?.deliveryInfo) {
      if (deliveryType === 'express' && orderData.product.deliveryInfo.expressDelivery?.isAvailable) {
        shipping = orderData.product.deliveryInfo.expressDelivery.cost || 50;
      } else if (deliveryType === 'standard') {
        shipping = orderData.product.deliveryInfo.standardDelivery?.cost || 0;
      }
    } else {
      // Fallback to old logic if deliveryInfo is not available
      shipping = discountedSubtotal < 500 ? 50 : 0;
    }

    const taxableAmount = discountedSubtotal + shipping;
    const gst = Math.round((taxableAmount * 0.18) * 100) / 100;
    const total = discountedSubtotal + shipping + gst;

    return {
      subtotal,
      discount,
      discountedSubtotal,
      shipping,
      gst,
      total
    };
  }, [orderData?.totalAmount, orderData?.product?.deliveryInfo, deliveryType, discountAmount]);

  // Check if orders are disabled
  useEffect(() => {
    if (isNotAcceptingOrders || isComingSoon) {
      setShowOrdersDisabledDialog(true);
    }
  }, [isNotAcceptingOrders, isComingSoon]);

  // Check authentication and redirect if not authenticated
  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated || !user) {
        navigate('/signin', { state: { from: location.pathname, orderData } });
        return;
      }
      if (!orderData) {
        navigate('/');
      }
    }
  }, [isAuthenticated, user, loading, navigate, orderData, location.pathname]);

  // Prevent staying on checkout page after successful payment
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        navigate('/my-orders');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal, navigate]);


  // Handle coupon application
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsValidatingCoupon(true);
    setCouponError('');

    try {
      const response = await apiService.validateCoupon(couponCode.trim(), orderData.totalAmount);

      if (response.status === 'success' && response.data) {
        setAppliedCoupon(response.data.coupon);
        setDiscountAmount(response.data.discount);
        setCouponError('');
        setCouponCode(''); // Clear input after successful application
      } else {
        setCouponError('Failed to apply coupon');
      }
    } catch (error) {
      console.error('Coupon validation error:', error);
      let errorMessage = 'Invalid coupon code';

      if (error.response) {
        // Handle API error response
        const responseData = error.response.data;
        if (responseData?.message) {
          errorMessage = responseData.message;
        } else if (responseData?.error?.message) {
          errorMessage = responseData.error.message;
        } else if (typeof responseData === 'string') {
          errorMessage = responseData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setCouponError(errorMessage);
      setAppliedCoupon(null);
      setDiscountAmount(0);
    } finally {
      setIsValidatingCoupon(false);
    }
  };

  // Handle coupon removal
  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscountAmount(0);
    setCouponCode('');
    setCouponError('');
  };

  // Handle payment
  const handlePayment = async () => {
    if (isNotAcceptingOrders || isComingSoon) {
      setShowOrdersDisabledDialog(true);
      return;
    }

    if ((deliveryType === 'standard' || deliveryType === 'express') && !shippingAddress) {
      alert('Please provide a valid shipping address to proceed with the order.');
      return;
    }

    if (!confirmed) {
      alert('Please confirm your order details before proceeding to payment.');
      return;
    }

    setIsLoading(true);
    try {
      let transformedDesignUpload = null;
      if (orderData.designData) {
        const transform = design => ({
          public_id: design.cloudinaryId || design.id,
          url: design.url,
          format: design.url?.split('.').pop()?.toLowerCase() || 'unknown',
          originalName: design.originalName || design.name,
          uploadedAt: design.uploadedAt || new Date().toISOString()
        });
        transformedDesignUpload = Array.isArray(orderData.designData)
          ? orderData.designData.map(transform)
          : transform(orderData.designData);
      }

      const finalOrderData = {
        productId: orderData.productId,
        quantity: orderData.selectedQuantity,
        customizations: {
          finish: orderData.selectedFinish,
          corners: orderData.selectedCorners,
          paperType: orderData.selectedPaperType,
          size: orderData.selectedSize
        },
        designUpload: transformedDesignUpload,
        shippingAddress: (deliveryType === 'standard' || deliveryType === 'express') ? shippingAddress : null,
        deliveryType,
        paymentMethod,
        couponCode: appliedCoupon?.code || null
      };

      const orderResponse = await apiService.createDirectOrder(finalOrderData);
      if (!orderResponse.success) {
        throw new Error(orderResponse.message || 'Failed to create order');
      }

      const paymentRequest = orderResponse.data?.paymentRequest;

      if (paymentMethod === 'payu') {
        if (!paymentRequest?.action || !paymentRequest?.fields) {
          throw new Error('PayU payment gateway response is incomplete');
        }

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = paymentRequest.action;
        form.style.display = 'none';

        Object.entries(paymentRequest.fields).forEach(([name, value]) => {
          if (value !== undefined && value !== null) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = String(value);
            form.appendChild(input);
          }
        });

        document.body.appendChild(form);
        form.submit();
      } else {
        if (!paymentRequest?.action || !paymentRequest?.encRequest || !paymentRequest?.access_code) {
          throw new Error('Payment gateway response is incomplete');
        }

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = paymentRequest.action;
        form.style.display = 'none';
        [
          ['encRequest', paymentRequest.encRequest],
          ['access_code', paymentRequest.access_code]
        ].forEach(([name, value]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
      }
    } catch (error) {
      console.error('Payment initialization error:', error);
      setPaymentFailureMessage(error.response?.data?.message || error.message || 'Payment initialization failed. Please try again.');
      setShowPaymentFailureDialog(true);
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#008000] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!orderData || !isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Checkout</h1>
          <p className="text-sm sm:text-base text-gray-600">Review your order and complete your purchase</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">

          {/* Left Column - Order Summary */}
          <div className="space-y-6">

            {/* Product Summary */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Order Summary</h2>

              {/* Product Details */}
              <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm mb-3 sm:mb-4">
                <div className="flex justify-between">
                  <span className="font-medium">Product:</span>
                  <span>{orderData.product?.name || 'Premium Business Cards'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Quantity:</span>
                  <span>{orderData.selectedQuantity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Finish:</span>
                  <span className="capitalize">{orderData.selectedFinish}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Corners:</span>
                  <span className="capitalize">{orderData.selectedCorners}</span>
                </div>
                {orderData.selectedSize && (
                  <div className="flex justify-between">
                    <span className="font-medium">Size:</span>
                    <span>{orderData.selectedSize}</span>
                  </div>
                )}
                {orderData.selectedPaperType && (
                  <div className="flex justify-between">
                    <span className="font-medium">Paper Type:</span>
                    <span>{orderData.selectedPaperType}</span>
                  </div>
                )}
                {orderData.designData ? (
                  <div className="flex justify-between">
                    <span className="font-medium">Design:</span>
                    <span className="text-[#008000]">
                      {Array.isArray(orderData.designData)
                        ? `${orderData.designData.length} Custom Upload${orderData.designData.length > 1 ? 's' : ''}`
                        : 'Custom Upload'}
                    </span>
                  </div>
                ) : (
                  <div className="flex justify-between">
                    <span className="font-medium">Design:</span>
                    <span className="text-orange-600">To be provided</span>
                  </div>
                )}
              </div>

              {/* Coupon Section */}
              <div className="border-t pt-3 sm:pt-4 mb-3 sm:mb-4">
                <h3 className="text-sm sm:text-base font-semibold mb-2 sm:mb-3">Have a Coupon Code?</h3>
                {!appliedCoupon ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value.toUpperCase());
                        setCouponError('');
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleApplyCoupon();
                        }
                      }}
                      placeholder="Enter coupon code"
                      className="flex-1 border border-gray-300 rounded-xl px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={isValidatingCoupon || !couponCode.trim()}
                      className="px-4 py-2 bg-[#008000] text-white rounded-xl text-xs sm:text-sm font-medium hover:bg-[#008000] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {isValidatingCoupon ? 'Applying...' : 'Apply'}
                    </button>
                  </div>
                ) : (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-[#008000]">
                          {appliedCoupon.code} Applied
                        </p>
                        {appliedCoupon.name && (
                          <p className="text-[10px] sm:text-xs text-[#008000] mt-0.5">
                            {appliedCoupon.name}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={handleRemoveCoupon}
                        className="text-[#008000] hover:text-[#008000] text-xs sm:text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
                {couponError && (
                  <p className="text-red-600 text-xs sm:text-sm mt-2">{couponError}</p>
                )}
              </div>


              {/* Price Breakdown */}
              {priceBreakdown && (
                <div className="border-t pt-3 sm:pt-4 space-y-2 sm:space-y-3">
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="text-gray-900">Rs.{priceBreakdown.subtotal.toFixed(2)}</span>
                  </div>
                  {priceBreakdown.discount > 0 && (
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Discount:</span>
                      <span className="text-[#008000] font-medium">-Rs.{priceBreakdown.discount.toFixed(2)}</span>
                    </div>
                  )}
                  {deliveryType !== 'self-pickup' && (
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Shipping ({deliveryType === 'express' ? 'Express' : 'Standard'}):</span>
                      {priceBreakdown.shipping > 0 ? (
                        <span className="text-gray-900">Rs.{priceBreakdown.shipping.toFixed(2)}</span>
                      ) : (
                        <span className="text-[#008000] font-medium">Free</span>
                      )}
                    </div>
                  )}
                  {deliveryType === 'self-pickup' && (
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Shipping:</span>
                      <span className="text-[#008000] font-medium">Self Pickup - Free</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs sm:text-sm">
                    <span className="text-gray-600">GST (18%):</span>
                    <span className="text-gray-900">Rs.{priceBreakdown.gst.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 sm:pt-3 mt-2 sm:mt-3">
                    <div className="flex justify-between text-base sm:text-lg font-bold">
                      <span>Total Amount:</span>
                      <span className="text-[#008000]">Rs.{priceBreakdown.total.toFixed(2)}</span>
                    </div>
                    <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                      You will be redirected to {paymentMethod === 'payu' ? 'PayU' : 'CCAvenue'} to complete payment
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Order Confirmation */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Confirm Your Order</h2>
              <div className="space-y-4">
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    className="mt-1 h-4 w-4 text-[#008000] focus:ring-green-500 border-gray-300 rounded"
                  />
                  <span className="text-xs sm:text-sm text-gray-700">
                    I confirm that all the product specifications and customizations are correct.
                    I understand that once the order is placed, changes may not be possible without additional charges.
                  </span>
                </label>

                <div className="text-[10px] sm:text-xs text-gray-500 space-y-0.5 sm:space-y-1">
                  <p>• Please review all specifications carefully before confirming</p>
                  <p>• Design files will be reviewed by our team for print quality</p>
                  <p>• You will receive order updates via email/SMS</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Delivery & Shipping Address */}
          <div className="space-y-6">

            {/* Delivery Type Selection */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Delivery Option</h2>
              <div className="space-y-3">
                <label className={`flex items-center p-3 sm:p-4 border-2 rounded-2xl cursor-pointer transition-all ${deliveryType === 'standard'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="radio"
                    name="deliveryType"
                    value="standard"
                    checked={deliveryType === 'standard'}
                    onChange={(e) => {
                      setDeliveryType(e.target.value);
                      if (e.target.value === 'self-pickup') {
                        setShippingAddress(null);
                      }
                    }}
                    className="h-4 w-4 text-[#008000] focus:ring-green-500 border-gray-300"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 text-[#008000] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
                        </svg>
                        <span className="font-medium text-sm sm:text-base text-gray-900">Standard Delivery</span>
                      </div>
                      <span className="text-xs sm:text-sm font-medium text-gray-700">
                        {orderData?.product?.deliveryInfo?.standardDelivery?.days || 3} days
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs sm:text-sm text-gray-600">Regular delivery to your address</p>
                      <span className="text-xs sm:text-sm font-semibold text-[#008000]">
                        {orderData?.product?.deliveryInfo?.standardDelivery?.cost > 0
                          ? `Rs.${orderData.product.deliveryInfo.standardDelivery.cost.toFixed(2)}`
                          : 'Free'}
                      </span>
                    </div>
                  </div>
                </label>

                {orderData?.product?.deliveryInfo?.expressDelivery?.isAvailable !== false && (
                  <label className={`flex items-center p-3 sm:p-4 border-2 rounded-2xl cursor-pointer transition-all ${deliveryType === 'express'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}>
                    <input
                      type="radio"
                      name="deliveryType"
                      value="express"
                      checked={deliveryType === 'express'}
                      onChange={(e) => {
                        setDeliveryType(e.target.value);
                        if (e.target.value === 'self-pickup') {
                          setShippingAddress(null);
                        }
                      }}
                      className="h-4 w-4 text-[#008000] focus:ring-green-500 border-gray-300"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-[#008000] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                          </svg>
                          <span className="font-medium text-sm sm:text-base text-gray-900">Express Delivery</span>
                        </div>
                        <span className="text-xs sm:text-sm font-medium text-gray-700">
                          {orderData?.product?.deliveryInfo?.expressDelivery?.days || 1} day
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-xs sm:text-sm text-gray-600">Fast delivery to your address</p>
                        <span className="text-xs sm:text-sm font-semibold text-[#008000]">
                          Rs.{(orderData?.product?.deliveryInfo?.expressDelivery?.cost || 50).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </label>
                )}

                <label className={`flex items-center p-3 sm:p-4 border-2 rounded-2xl cursor-pointer transition-all ${deliveryType === 'self-pickup'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="radio"
                    name="deliveryType"
                    value="self-pickup"
                    checked={deliveryType === 'self-pickup'}
                    onChange={(e) => {
                      setDeliveryType(e.target.value);
                      if (e.target.value === 'self-pickup') {
                        setShippingAddress(null);
                      }
                    }}
                    className="h-4 w-4 text-[#008000] focus:ring-green-500 border-gray-300"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-[#008000] mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                      <span className="font-medium text-sm sm:text-base text-gray-900">Self Pickup</span>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Pick up your order from our store</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Shipping Address - Only show for delivery */}
            {(deliveryType === 'standard' || deliveryType === 'express') && (
              <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-200">
                <ShippingAddressForm
                  onAddressChange={setShippingAddress}
                  initialAddress={user ? {
                    firstName: user.name?.split(' ')[0] || '',
                    lastName: user.name?.split(' ').slice(1).join(' ') || '',
                    phone: user.phone || '',
                    addressLine1: user.address?.street || '',
                    city: user.address?.city || '',
                    state: user.address?.state || '',
                    pincode: user.address?.postalCode || '',
                    country: user.address?.country || 'India'
                  } : {}}
                />
              </div>
            )}

            {/* Payment Section */}
            <div className="bg-white rounded-2xl p-4 sm:p-6 shadow-lg border border-gray-200">
              <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Payment Method</h2>

              {/* Payment Method Selector - PayU only for customer checkout */}
              <div className="space-y-3 mb-6">
                {/* CCAvenue integration is preserved in codebase and can be re-enabled here anytime */}
                {/*
                <label className={`flex items-center p-3 sm:p-4 border-2 rounded-2xl cursor-pointer transition-all ${paymentMethod === 'ccavenue'
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="ccavenue"
                    checked={paymentMethod === 'ccavenue'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="h-4 w-4 text-[#008000] focus:ring-green-500 border-gray-300"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm sm:text-base text-gray-900">CCAvenue</span>
                      <span className="text-xs font-medium text-gray-500">Cards, NetBanking, UPI</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">Pay via CCAvenue Gateway</p>
                  </div>
                </label>
                */}

                <label className="flex items-center p-3 sm:p-4 border-2 rounded-2xl cursor-pointer transition-all border-green-500 bg-green-50">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="payu"
                    checked={paymentMethod === 'payu'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="h-4 w-4 text-[#008000] focus:ring-green-500 border-gray-300"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm sm:text-base text-gray-900">PayU</span>
                      <span className="text-xs font-medium text-gray-500">Cards, NetBanking, UPI</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">Pay via PayU Hosted Checkout</p>
                  </div>
                </label>
              </div>

              {/* Security Note */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 mb-4 sm:mb-6">
                <div className="flex items-start space-x-1.5 sm:space-x-2">
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#008000] mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 1L5 6v6l5 5 5-5V6l-5-5zM8.5 6L10 4.5 11.5 6v6L10 13.5 8.5 12V6z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-[#008000]">Secure Payment</p>
                    <p className="text-[10px] sm:text-xs text-[#008000]">Your payment information is encrypted and secure</p>
                  </div>
                </div>
              </div>

              {/* Place Order Button */}
              <button
                onClick={handlePayment}
                disabled={isLoading || ((deliveryType === 'standard' || deliveryType === 'express') && !shippingAddress) || !confirmed || isNotAcceptingOrders || isComingSoon}
                className={`w-full py-3 sm:py-4 rounded-2xl font-semibold text-sm sm:text-lg flex items-center justify-center transition-all duration-200 shadow-lg hover:shadow-xl ${((deliveryType === 'standard' || deliveryType === 'express') && !shippingAddress) || !confirmed || isNotAcceptingOrders || isComingSoon
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-[#008000] hover:bg-[#008000] disabled:bg-gray-400 text-white transform hover:scale-105'
                  }`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing Payment...
                  </>
                ) : (deliveryType === 'standard' || deliveryType === 'express') && !shippingAddress ? (
                  'Add Shipping Address'
                ) : !confirmed ? (
                  'Confirm Order Details'
                ) : (
                  `Place Order - Rs.${priceBreakdown?.total.toFixed(2) || orderData.totalAmount}`
                )}
              </button>

              {(((deliveryType === 'standard' || deliveryType === 'express') && !shippingAddress) || !confirmed) && (
                <p className="text-xs sm:text-sm text-gray-500 text-center mt-2">
                  {(deliveryType === 'standard' || deliveryType === 'express') && !shippingAddress ? 'Please add a shipping address to continue' : 'Please confirm your order details to continue'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Back to Product Button */}
        <div className="mt-6 sm:mt-8 text-center">
          <button
            onClick={() => navigate(-1)}
            className="text-sm sm:text-base text-gray-600 hover:text-gray-800 font-medium"
          >
            ← Back to Product
          </button>
        </div>
      </div>

      {showSuccessModal && successOrder && (
        <OrderSuccessModal
          order={successOrder}
          onClose={() => {
            setShowSuccessModal(false);
            navigate('/my-orders');
          }}
          onViewOrders={() => {
            setShowSuccessModal(false);
            navigate('/my-orders');
          }}
        />
      )}

      <PaymentFailureDialog
        isOpen={showPaymentFailureDialog}
        onClose={() => setShowPaymentFailureDialog(false)}
        errorMessage={paymentFailureMessage}
      />

      {showOrdersDisabledDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Orders Currently Unavailable
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                We are currently not accepting orders. Please check back later. We apologize for any inconvenience.
              </p>
              <button
                onClick={() => {
                  setShowOrdersDisabledDialog(false);
                  navigate('/');
                }}
                className="w-full bg-[#008000] text-white py-2 px-4 rounded-lg font-medium hover:bg-[#008000] transition-colors"
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
